import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, RefreshCw, LogOut, ArrowUpRight } from "lucide-react";
import ScrollProgress from "../components/site/ScrollProgress";

const TOKEN_KEY = "rp_admin_token";

export default function AdminLeads() {
  const [token, setToken] = useState(() => {
    try { return window.localStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
  });
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API = process.env.REACT_APP_BACKEND_URL;

  const fetchAll = async (tk) => {
    setLoading(true); setError(null);
    try {
      const headers = { Authorization: `Bearer ${tk}` };
      const [s, l] = await Promise.all([
        fetch(`${API}/api/admin/stats`, { headers }),
        fetch(`${API}/api/admin/leads?limit=50`, { headers }),
      ]);
      if (s.status === 401 || l.status === 401) throw new Error("Invalid token");
      if (!s.ok || !l.ok) throw new Error(`HTTP ${s.status} / ${l.status}`);
      setStats(await s.json());
      setLeads(await l.json());
    } catch (e) {
      setError(e.message);
      setStats(null); setLeads(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchAll(token);
  }, [token]); // eslint-disable-line

  const onSubmitGate = (e) => {
    e.preventDefault();
    const t = new FormData(e.target).get("token")?.toString().trim();
    if (!t) return;
    try { window.localStorage.setItem(TOKEN_KEY, t); } catch { /* ignore */ }
    setToken(t);
  };

  const onLogout = () => {
    try { window.localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
    setToken(""); setStats(null); setLeads(null);
  };

  if (!token || (error === "Invalid token")) {
    return <Gate onSubmit={onSubmitGate} error={error} />;
  }

  return (
    <div className="min-h-screen bg-ink text-bone">
      <AdminTopBar onLogout={onLogout} onRefresh={() => fetchAll(token)} loading={loading} />
      <ScrollProgress />

      <main className="mx-auto max-w-[1440px] px-6 lg:px-10 py-12 lg:py-16">
        <header className="mb-10 lg:mb-14">
          <p className="text-xs uppercase tracking-wider2 text-ember mb-4 font-bold">Internal · Lead intelligence</p>
          <h1 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)" }}>
            Leads<span className="italic font-light text-ember"> dashboard.</span>
          </h1>
        </header>

        {error && error !== "Invalid token" && (
          <div className="mb-10 border border-ember/40 bg-ember/5 p-5 text-sm text-ember">
            {error}
          </div>
        )}

        {!stats && loading && (
          <p className="text-bone/60 text-sm">Loading…</p>
        )}

        {stats && (
          <>
            <TotalsRow t={stats.totals} />
            <AbBlock ab={stats.ab} />
            <BreakdownsRow stats={stats} />
            <TimelineBlock data={stats.timeline_30d} />
            {leads && <LeadsTable leads={leads} />}
          </>
        )}
      </main>
    </div>
  );
}

// ─── Gate (token entry) ─────────────────────────────────────────────
const Gate = ({ onSubmit, error }) => (
  <div className="min-h-screen bg-ink text-bone flex items-center justify-center p-6">
    <form onSubmit={onSubmit} className="w-full max-w-md" data-testid="admin-gate">
      <p className="text-xs uppercase tracking-wider2 text-ember mb-4 font-bold flex items-center gap-2">
        <Lock size={12} strokeWidth={2} /> Internal access
      </p>
      <h1 className="font-display text-5xl tracking-editorial leading-[0.95] mb-8">
        Admin<span className="italic font-light text-ember"> sign-in.</span>
      </h1>
      <label className="block">
        <span className="text-xs uppercase tracking-wider2 font-bold text-bone/65 block mb-2">Admin token</span>
        <input
          name="token"
          type="password"
          autoFocus
          placeholder="••••••••••••••••••••"
          data-testid="admin-token-input"
          className="w-full bg-transparent border-b border-bone/20 focus:border-ember text-bone placeholder:text-bone/30 font-sans font-bold tracking-tight text-xl pb-3 focus:outline-none transition-colors"
        />
      </label>
      {error === "Invalid token" && (
        <p className="mt-4 text-xs uppercase tracking-wider2 font-bold text-ember">Invalid token — please try again.</p>
      )}
      <button
        type="submit"
        data-testid="admin-gate-submit"
        className="mt-10 w-full bg-ember text-bone font-sans font-bold uppercase tracking-wider2 text-sm px-6 py-4 hover:bg-ember-deep transition-colors"
      >
        Enter Dashboard
      </button>
      <Link to="/" className="block mt-6 text-xs uppercase tracking-wider2 text-bone/45 hover:text-bone transition-colors text-center">← Back to site</Link>
    </form>
  </div>
);

// ─── Top bar ────────────────────────────────────────────────────────
const AdminTopBar = ({ onLogout, onRefresh, loading }) => (
  <header className="sticky top-0 z-50 bg-ink/95 backdrop-blur-md border-b border-bone/10">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10 py-4 flex items-center justify-between">
      <Link to="/" className="font-display text-lg tracking-editorial">
        Restaurant<span className="italic font-light text-ember"> Photographer</span>
        <span className="ml-3 text-[10px] uppercase tracking-wider2 font-bold text-ember/80">/ Admin</span>
      </Link>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          data-testid="admin-refresh"
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-wider2 font-bold text-bone/75 hover:text-ember transition-colors px-3 py-2"
        >
          <RefreshCw size={12} strokeWidth={2} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
        <button
          type="button"
          onClick={onLogout}
          data-testid="admin-logout"
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-wider2 font-bold text-bone/75 hover:text-ember transition-colors px-3 py-2"
        >
          <LogOut size={12} strokeWidth={2} /> Sign out
        </button>
      </div>
    </div>
  </header>
);

// ─── Totals row ─────────────────────────────────────────────────────
const TotalsRow = ({ t }) => {
  const items = [
    { label: "All time", value: t.all_time },
    { label: "This month", value: t.this_month },
    { label: "This week", value: t.this_week },
    { label: "Today", value: t.today },
    { label: "Pipeline est.", value: `$${(t.pipeline_estimate_aud || 0).toLocaleString()}` },
  ];
  return (
    <section className="grid grid-cols-2 md:grid-cols-5 gap-px bg-bone/10 border border-bone/10 mb-10">
      {items.map((i) => (
        <div key={i.label} className="bg-ink p-6 md:p-8">
          <p className="text-[10px] uppercase tracking-wider2 text-ember mb-3 font-bold">{i.label}</p>
          <p className="font-sans font-bold tracking-tight leading-none" style={{ fontSize: "clamp(1.5rem, 2.5vw, 2.25rem)" }}>{i.value}</p>
        </div>
      ))}
    </section>
  );
};

// ─── A/B block ──────────────────────────────────────────────────────
const AbBlock = ({ ab }) => {
  const total = ab.A.count + ab.B.count;
  const pctA = total ? Math.round((ab.A.count / total) * 100) : 0;
  const pctB = total ? 100 - pctA : 0;
  const winner = ab.A.count > ab.B.count ? "A" : ab.B.count > ab.A.count ? "B" : null;
  return (
    <section className="bg-[#161613] p-7 md:p-10 mb-10 border border-bone/10">
      <div className="flex items-baseline justify-between gap-4 mb-7">
        <div>
          <p className="text-[10px] uppercase tracking-wider2 text-ember font-bold mb-2">A/B test · Sticky CTA label</p>
          <h2 className="font-display text-2xl md:text-3xl tracking-editorial leading-tight">
            {winner ? <>Variant <span className="text-ember italic">{winner}</span> is leading.</> : "Too close to call."}
          </h2>
        </div>
        <p className="text-xs text-bone/50">Untagged: {ab.untagged}</p>
      </div>

      <div className="space-y-5">
        <Bar label={ab.A.label} count={ab.A.count} pct={pctA} highlight={winner === "A"} />
        <Bar label={ab.B.label} count={ab.B.count} pct={pctB} highlight={winner === "B"} />
      </div>
    </section>
  );
};

const Bar = ({ label, count, pct, highlight }) => (
  <div>
    <div className="flex items-baseline justify-between mb-2">
      <span className={`text-sm font-bold tracking-tight ${highlight ? "text-ember" : "text-bone/85"}`}>{label}</span>
      <span className="font-sans font-bold tracking-tight">{count} <span className="text-bone/45 text-xs">· {pct}%</span></span>
    </div>
    <div className="h-2 bg-bone/10">
      <div className={`h-full transition-all duration-700 ${highlight ? "bg-ember" : "bg-bone/45"}`} style={{ width: `${pct}%` }} />
    </div>
  </div>
);

// ─── Breakdowns row ─────────────────────────────────────────────────
const BreakdownsRow = ({ stats }) => {
  const blocks = [
    { title: "By source", data: stats.by_source },
    { title: "By suburb", data: stats.by_suburb },
    { title: "By cuisine", data: stats.by_cuisine },
    { title: "By channel", data: stats.by_channel },
    { title: "By timing", data: stats.by_timing },
    { title: "By project type", data: stats.by_project_type },
  ];
  return (
    <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-bone/10 border border-bone/10 mb-10">
      {blocks.map((b) => (
        <div key={b.title} className="bg-ink p-6 md:p-7">
          <p className="text-[10px] uppercase tracking-wider2 text-ember mb-5 font-bold">{b.title}</p>
          {b.data.length === 0 ? (
            <p className="text-sm text-bone/40 italic">No data yet.</p>
          ) : (
            <ul className="space-y-3">
              {b.data.slice(0, 8).map((r) => (
                <li key={r.name} className="flex items-baseline justify-between border-b border-bone/5 pb-2">
                  <span className="text-sm text-bone/85">{r.name}</span>
                  <span className="font-sans font-bold text-base tracking-tight">{r.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </section>
  );
};

// ─── Timeline ───────────────────────────────────────────────────────
const TimelineBlock = ({ data }) => {
  const max = Math.max(...data.map((d) => d.count), 1);
  // Build 30-day window for consistent x-axis
  const days = useMemo(() => {
    const map = Object.fromEntries(data.map((d) => [d.date, d.count]));
    const out = [];
    for (let i = 29; i >= 0; i--) {
      const dt = new Date(Date.now() - i * 86400000);
      const key = dt.toISOString().slice(0, 10);
      out.push({ date: key, count: map[key] || 0 });
    }
    return out;
  }, [data]);

  return (
    <section className="bg-ink p-7 md:p-10 mb-10 border border-bone/10">
      <p className="text-[10px] uppercase tracking-wider2 text-ember font-bold mb-3">Last 30 days · Daily leads</p>
      <h2 className="font-display text-2xl md:text-3xl tracking-editorial leading-tight mb-8">Cadence.</h2>
      <div className="flex items-end gap-1 h-40">
        {days.map((d) => (
          <div key={d.date} className="flex-1 flex flex-col items-center justify-end group" title={`${d.date} · ${d.count}`}>
            <div
              className={`w-full transition-colors ${d.count > 0 ? "bg-ember group-hover:bg-bone" : "bg-bone/10"}`}
              style={{ height: `${(d.count / max) * 100}%`, minHeight: d.count > 0 ? 4 : 2 }}
            />
          </div>
        ))}
      </div>
      <div className="flex justify-between mt-3 text-[10px] uppercase tracking-wider2 text-bone/40 font-bold">
        <span>{days[0]?.date.slice(5)}</span>
        <span>{days[days.length - 1]?.date.slice(5)}</span>
      </div>
    </section>
  );
};

// ─── Recent leads table ─────────────────────────────────────────────
const LeadsTable = ({ leads }) => (
  <section data-testid="admin-leads-table">
    <div className="flex items-baseline justify-between mb-6">
      <div>
        <p className="text-[10px] uppercase tracking-wider2 text-ember font-bold mb-2">Recent leads</p>
        <h2 className="font-display text-2xl md:text-3xl tracking-editorial leading-tight">Inbox.</h2>
      </div>
      <p className="text-xs text-bone/45">Showing {leads.length}</p>
    </div>

    {leads.length === 0 ? (
      <p className="text-sm text-bone/40 italic p-10 border border-bone/10">No leads yet.</p>
    ) : (
      <div className="overflow-x-auto border border-bone/10">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#161613] text-bone/55 text-[10px] uppercase tracking-wider2 font-bold">
              <th className="text-left py-3 px-4">When</th>
              <th className="text-left py-3 px-4">Venue / Name</th>
              <th className="text-left py-3 px-4">Suburb</th>
              <th className="text-left py-3 px-4">Source</th>
              <th className="text-left py-3 px-4">CTA</th>
              <th className="text-right py-3 px-4">Est.</th>
              <th className="text-right py-3 px-4">Contact</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id} className="border-t border-bone/5 hover:bg-[#161613] transition-colors">
                <td className="py-3 px-4 text-bone/65 text-xs">{(l.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="py-3 px-4">
                  <p className="font-display tracking-editorial text-base">{l.restaurant || "—"}</p>
                  <p className="text-xs text-bone/55">{l.name}</p>
                </td>
                <td className="py-3 px-4 text-bone/75">{l.suburb || "—"}</td>
                <td className="py-3 px-4 text-bone/75 text-xs">{l.source}</td>
                <td className="py-3 px-4">
                  {l.cta_variant ? (
                    <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider2 font-bold ${
                      l.cta_variant === "A" ? "bg-ember/20 text-ember" : "bg-bone/15 text-bone"
                    }`}>{l.cta_variant}</span>
                  ) : <span className="text-bone/30 text-xs">—</span>}
                </td>
                <td className="py-3 px-4 text-right font-sans font-bold tracking-tight">{l.estimate_total ? `$${l.estimate_total.toLocaleString()}` : "—"}</td>
                <td className="py-3 px-4 text-right">
                  <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1 text-xs text-bone/65 hover:text-ember transition-colors">
                    Email <ArrowUpRight size={11} strokeWidth={2} />
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </section>
);
